import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { HOST } from '../../config';
import {
  buildDashboardUrlReply,
  buildSellerDashboardContext,
  isDashboardUrlQuestion,
  resolveDashboardUrlRequest,
} from './sellerDashboardLinks';
import { buildSellerSupportContext } from './sellerSupportKnowledge';
import { getAgentErrorMessage } from './agentErrors';

const SESSION_KEY = 'seller_agent_session_v1';
const MESSAGES_KEY = 'seller_agent_messages_v1';

const PAGE_CONTEXT_MAP = {
  '/dashboard/products': 'Product Catalog — seller can view, add, edit products',
  '/dashboard/product/new': 'Create New Product — seller is adding a product',
  '/dashboard/subscription': 'Subscription — seller manages their plan',
  '/dashboard/sale': 'Sales & Promotions — seller manages discounts and sales',
  '/dashboard/order': 'Orders — seller views and manages customer orders',
  '/dashboard/chat': 'Customer Chats — seller communicates with buyers',
  '/dashboard/blog': 'Blog — seller manages blog posts',
  '/dashboard/printful': 'Printful — seller manages print-on-demand products',
  '/dashboard/user': 'Account Settings — seller manages profile',
};

const getPageContext = (pathname) => {
  const entry = Object.entries(PAGE_CONTEXT_MAP).find(([key]) => pathname.includes(key));
  return entry ? entry[1] : 'Seller Dashboard';
};

const getJwt = () => localStorage.getItem('accessToken') || '';

const getOrCreateSession = () => {
  const existing = localStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const id = `seller-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(SESSION_KEY, id);
  return id;
};

const loadMessages = () => {
  try {
    return JSON.parse(localStorage.getItem(MESSAGES_KEY) || '[]');
  } catch {
    return [];
  }
};

const makeMsg = (role, content, metadata = null, extras = {}) => ({
  id: `${Date.now()}-${Math.random()}`,
  role,
  content,
  metadata,
  timestamp: new Date().toISOString(),
  ...extras,
});

const uploadAgentFiles = async (files) => {
  if (!files?.length) return [];
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const response = await fetch(`${HOST}/agent/upload`, {
    method: 'POST',
    body: formData,
  });
  const payload = await response.json();
  if (!response.ok) {
    throw new Error(getAgentErrorMessage(payload?.error || 'Failed', { context: 'upload' }));
  }
  return payload.map((item, index) => ({
    url: item.url,
    name: item.originalName || files[index]?.name || 'Attachment',
    type: item.type || files[index]?.type || 'document',
    storeImagePath: item.storeImagePath || null,
  }));
};

export default function useSellerAgent() {
  const { pathname } = useLocation();
  const [messages, setMessages] = useState(loadMessages);
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(getOrCreateSession);
  const [greeted, setGreeted] = useState(() => messages.length > 0);

  const persist = useCallback((msgs) => {
    setMessages(msgs);
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(msgs));
  }, []);

  const sendToApi = useCallback(
    async (text, currentMessages, pathnameForContext, attachments = []) => {
      const fileUrls = attachments.map((item) => item.url).filter(Boolean);
      const supportContext = buildSellerSupportContext(
        text,
        pathnameForContext || pathname,
        attachments
      );
      const response = await fetch(`${HOST}/agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          message: text,
          jwtToken: getJwt(),
          preferredLanguage: 'en',
          fileUrls,
          sellerDashboardContext: buildSellerDashboardContext(pathnameForContext || pathname),
          sellerSupportContext: supportContext,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(getAgentErrorMessage(payload?.error || 'Failed', { context: 'chat' }));
      }
      const reply = (payload?.reply || 'Done.').replace('$$SECURE_PASSWORD_REQUIRED$$', '').trim();
      const botMsg = makeMsg('bot', reply, payload?.metadata || null);
      persist([...currentMessages, botMsg]);
    },
    [sessionId, persist, pathname]
  );

  const openChat = useCallback(async () => {
    setIsOpen(true);
    if (greeted) return;
    setGreeted(true);
    setIsTyping(true);
    const context = getPageContext(pathname);
    const greetMsg = `[SELLER_DASHBOARD_CONTEXT] Seller just opened the AI assistant. Current page: ${context}. Greet them warmly as AIRStudio seller support — you help with setup AND fixing platform issues (images, products, subscription, orders). Check approval status and offer help for this section.`;
    try {
      await sendToApi(greetMsg, messages);
    } catch (error) {
      persist([
        ...messages,
        makeMsg('bot', getAgentErrorMessage(error, { context: 'chat' })),
      ]);
    } finally {
      setIsTyping(false);
    }
  }, [greeted, pathname, messages, sendToApi]);

  const sendMessage = useCallback(
    async (text, files = []) => {
      const value = String(text || '').trim();
      if (!value && !files.length) return;

      setIsTyping(true);
      let attachments = [];

      try {
        if (files.length) {
          attachments = await uploadAgentFiles(files);
        }
      } catch (error) {
        setIsTyping(false);
        persist([
          ...messages,
          makeMsg('bot', getAgentErrorMessage(error, { context: 'upload' })),
        ]);
        return;
      }

      const userMsg = makeMsg(
        'user',
        value || 'Please review the attached file(s).',
        null,
        {
          attachments,
          fileUrls: attachments.map((item) => item.url),
        }
      );
      const next = [...messages, userMsg];
      persist(next);

      if (!files.length && value && isDashboardUrlQuestion(value)) {
        const resolved = resolveDashboardUrlRequest(value, pathname);
        const reply = buildDashboardUrlReply(value, pathname);
        persist([
          ...next,
          makeMsg(
            'bot',
            reply,
            resolved.type === 'single'
              ? { type: 'dashboard_link', data: { url: resolved.url, label: resolved.label } }
              : { type: 'dashboard_links' }
          ),
        ]);
        setIsTyping(false);
        return;
      }

      try {
        await sendToApi(
          value || 'Please process the attached file(s).',
          next,
          pathname,
          attachments
        );
      } catch (error) {
        persist([
          ...next,
          makeMsg('bot', getAgentErrorMessage(error, { context: 'chat' })),
        ]);
      } finally {
        setIsTyping(false);
      }
    },
    [messages, persist, sendToApi, pathname]
  );

  const clearChat = useCallback(() => {
    persist([]);
    setGreeted(false);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(MESSAGES_KEY);
  }, [persist]);

  return useMemo(
    () => ({
      messages,
      isOpen,
      isTyping,
      sessionId,
      pageContext: getPageContext(pathname),
      sendMessage,
      openChat,
      closeChat: () => setIsOpen(false),
      clearChat,
    }),
    [messages, isOpen, isTyping, sessionId, pathname, sendMessage, openChat, clearChat]
  );
}
